import React, { useCallback, useState } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import Button from '@atlaskit/button/standard-button';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import MoreIcon from '@atlaskit/icon/glyph/more';
import Popup, { type TriggerProps } from '@atlaskit/popup';
import { token } from '@atlaskit/tokens';
import type { ActiveKeyMap } from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-registry.tsx';
import Shortcuts from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcuts/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useIsFullscreen } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/fullscreen/index.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useBacklogSelector } from '../../../state/index.tsx';
import { getIsCompanyManaged } from '../../../state/selectors/app/index.tsx';
import { isBacklogEmptySelector } from '../../../state/selectors/cards/index.tsx';
import { getShowEpicAsPanel } from '../../../state/selectors/config/index.tsx';
import { canEditBoardConfig } from '../../../state/selectors/permissions/index.tsx';
import { isVersionsFeatureEnabled } from '../../../state/selectors/version/index.tsx';
import MenuContent from './content/index.tsx';
import messages from './messages.tsx';

const BacklogPopupMenu = () => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isOpen, setIsOpen] = useState<boolean>(false);
	// from old props
	const isCompanyManaged = useBacklogSelector(getIsCompanyManaged);
	const canEditBoardConfigProp = useBacklogSelector(canEditBoardConfig);
	const shouldShowDetailViewItem = useBacklogSelector((state) => !isBacklogEmptySelector(state));
	const shouldShowEpicAsPanel = useBacklogSelector(getShowEpicAsPanel);
	const shouldShowVersionsPanel = useBacklogSelector(isVersionsFeatureEnabled);

	const handleClick = useCallback(
		(_: React.MouseEvent<HTMLElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
			setIsOpen(!isOpen);
			analyticsEvent.update({ action: 'toggled', actionSubject: 'dropdownMenu' });
			fireUIAnalytics(analyticsEvent, 'backlogHeaderMenu');
		},
		[isOpen],
	);

	const handleClose = useCallback(() => {
		setIsOpen(false);
		const analyticsEvent = createAnalyticsEvent({
			action: 'toggled',
			actionSubject: 'dropdownMenu',
		});
		fireUIAnalytics(analyticsEvent, 'backlogHeaderMenu');
	}, [createAnalyticsEvent]);

	const getMoreIcon = () =>
		fg('tnk-1620-visual-refresh-software-backlog-icons') ? (
			<ShowMoreHorizontalIcon
				label={formatMessage(messages.dropdownAriaLabel)}
				color={token('color.icon')}
				spacing="spacious"
				LEGACY_fallbackIcon={MoreIcon}
				LEGACY_primaryColor={token('color.icon', '#44546F')}
			/>
		) : (
			<MoreIcon
				label={formatMessage(messages.dropdownAriaLabel)}
				primaryColor={token('color.icon', '#44546F')}
			/>
		);

	const [isFullscreen, { setIsFullscreen }] = useIsFullscreen();

	const toggleFullscreen = useCallback(() => {
		setIsFullscreen(!isFullscreen);
	}, [isFullscreen, setIsFullscreen]);

	const shortcutsKeyMap: ActiveKeyMap = {
		z: {
			callback: toggleFullscreen,
		},
	};

	const renderTrigger = (triggerProps: TriggerProps) => (
		<>
			{isVisualRefreshEnabled() || getWillShowNav4() ? (
				<IconButton
					{...triggerProps}
					icon={ShowMoreHorizontalIcon}
					label={formatMessage(messages.buttonLabel)}
					isSelected={isOpen}
					onClick={handleClick}
					isTooltipDisabled={false}
				/>
			) : (
				<Button
					{...triggerProps}
					iconBefore={getMoreIcon()}
					isSelected={isOpen}
					onClick={handleClick}
				/>
			)}
		</>
	);

	const renderContent = () => (
		<MenuContent
			canEditBoardConfig={canEditBoardConfigProp}
			isCompanyManaged={isCompanyManaged}
			shouldShowDetailViewItem={shouldShowDetailViewItem}
			shouldShowEpicAsPanel={shouldShowEpicAsPanel}
			shouldShowVersionsPanel={shouldShowVersionsPanel}
			onClosePopup={handleClose}
		/>
	);

	return (
		<>
			<Popup
				placement="bottom-end"
				isOpen={isOpen}
				onClose={handleClose}
				content={renderContent}
				trigger={renderTrigger}
			/>
			{getWillShowNav4() && fg('jira_nav4_beta_drop_1') && <Shortcuts keyMap={shortcutsKeyMap} />}
		</>
	);
};

export default BacklogPopupMenu;
